import classNames from 'classnames'
import { useRef, useState, type ReactElement } from 'react'
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type { ColumnDef, ColumnSort, SortingState } from '@tanstack/table-core'

import Text from '@/elements/Text'
import useBreakpoints from '@/hooks/useBreakpoints'
import useOverflow from '@/hooks/useOverflow'
import './Table.scss'

interface TableProps<T> {
  data: T[]
  columns: ColumnDef<T>[]
  sortees?: ColumnSort[]
  renderOptions?: (data: T) => ReactElement
}

function Table<T extends object>({
  data,
  columns,
  sortees = [],
  renderOptions,
}: TableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(sortees)
  const tableRef = useRef<HTMLDivElement | null>(null)
  const { isMobile } = useBreakpoints()
  const { hasOverflow } = useOverflow(tableRef)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: true,
    state: { sorting },
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: false,
  })

  return (
    <div
      className={classNames('table-wrapper', {
        'has-overflow': hasOverflow,
      })}
      ref={tableRef}
      data-overflow={hasOverflow}
    >
      <table>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const { column } = header
                const isSortable = column.getCanSort()
                const isSorted = column.getIsSorted()

                const columnWidth = (column.columnDef.meta as { width?: string })?.width ?? 'auto'
                const className = `column-header ${isSortable ? 'sortable' : ''} ${isSorted ? 'sorted' : ''}`

                return (
                  <th key={header.id} style={{ width: columnWidth }} className={!isSortable ? 'disabled ' : ''}>
                    <div
                      className={className}
                      onClick={isSortable && column.getToggleSortingHandler() || undefined}
                    >
                      <Text variant='footnote' color={isSorted ? 'primary' : 'black-light-40'}>
                        {flexRender(column.columnDef.header, header.getContext())}
                      </Text>
                      {(isSortable && {
                        asc: <ChevronDown size={18} />,
                        desc: <ChevronUp size={18} />,
                      }[isSorted as string])
                        ?? <ChevronsUpDown size={18} />}
                    </div>
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  const data = cell.row.original
                  let toRender
                  if (renderOptions && cell.column.id === 'actions') {
                    toRender = <div>{renderOptions(data)}</div>
                  } else {
                    toRender = flexRender(cell.column.columnDef.cell, cell.getContext())
                  }
                  if (isMobile) {
                    const heading = flexRender(cell.column.columnDef.header, cell.getContext() as any)
                    toRender = (
                      <>
                        <Text variant='footnote' color='black-light-60' className='cell-heading'>
                          {heading}:
                        </Text>
                        <span className='cell-content'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </span>
                      </>
                    )
                  }
                  return <td key={cell.id}>{toRender}</td>
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className='empty-cell'>
                <Text color='black-light-60'>No data available...</Text>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default Table
